// Floating, draggable calculator + tools. Rendered once at the app root and
// toggled from a header button, so it floats over ANY view and stays usable
// while the agent works (non-blocking — it does not dim the screen).
// Tabs: Calculator · Unit converter · Commission · Currency · History.
// 100% client-side except the Currency tab, which fetches daily FX rates from
// the public, no-key endpoint open.er-api.com (whitelisted in the CSP
// connect-src) and caches the last response in localStorage for offline use.

import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Calculator, ArrowLeftRight, Delete, Clock, Save, Trash2, RefreshCw } from 'lucide-react';

// ── Unit-conversion catalog ───────────────────────────────────────────────
// Linear categories store each unit's factor to a common base unit; converting
// is value*fromFactor/toFactor. Temperature is non-linear, handled separately.
const CONV = {
  length: {
    name: { es: 'Longitud', en: 'Length' },
    units: [
      { id: 'mm', label: 'mm', f: 0.001 }, { id: 'cm', label: 'cm', f: 0.01 },
      { id: 'm', label: 'm', f: 1 }, { id: 'km', label: 'km', f: 1000 },
      { id: 'in', label: 'in', f: 0.0254 }, { id: 'ft', label: 'ft', f: 0.3048 },
      { id: 'yd', label: 'yd', f: 0.9144 }, { id: 'mi', label: 'mi', f: 1609.344 },
    ],
  },
  weight: {
    name: { es: 'Peso / Masa', en: 'Weight / Mass' },
    units: [
      { id: 'mg', label: 'mg', f: 0.001 }, { id: 'g', label: 'g', f: 1 },
      { id: 'kg', label: 'kg', f: 1000 }, { id: 't', label: 't (métrica)', f: 1e6 },
      { id: 'oz', label: 'oz', f: 28.349523125 }, { id: 'lb', label: 'lb', f: 453.59237 },
      { id: 'st', label: 'stone', f: 6350.29318 },
    ],
  },
  temperature: {
    name: { es: 'Temperatura', en: 'Temperature' },
    special: 'temp',
    units: [{ id: 'C', label: '°C' }, { id: 'F', label: '°F' }, { id: 'K', label: 'K' }],
  },
  volume: {
    name: { es: 'Volumen', en: 'Volume' },
    units: [
      { id: 'ml', label: 'mL', f: 0.001 }, { id: 'l', label: 'L', f: 1 },
      { id: 'm3', label: 'm³', f: 1000 },
      { id: 'tsp', label: 'tsp (US)', f: 0.00492892 }, { id: 'tbsp', label: 'tbsp (US)', f: 0.0147868 },
      { id: 'floz', label: 'fl oz (US)', f: 0.0295735 }, { id: 'cup', label: 'cup (US)', f: 0.236588 },
      { id: 'pt', label: 'pt (US)', f: 0.473176 }, { id: 'qt', label: 'qt (US)', f: 0.946353 },
      { id: 'gal', label: 'gal (US)', f: 3.78541 },
    ],
  },
  area: {
    name: { es: 'Área', en: 'Area' },
    units: [
      { id: 'cm2', label: 'cm²', f: 0.0001 }, { id: 'm2', label: 'm²', f: 1 },
      { id: 'km2', label: 'km²', f: 1e6 }, { id: 'ha', label: 'ha', f: 10000 },
      { id: 'ft2', label: 'ft²', f: 0.09290304 }, { id: 'yd2', label: 'yd²', f: 0.83612736 },
      { id: 'acre', label: 'acre', f: 4046.8564224 }, { id: 'mi2', label: 'mi²', f: 2589988.110336 },
    ],
  },
  speed: {
    name: { es: 'Velocidad', en: 'Speed' },
    units: [
      { id: 'mps', label: 'm/s', f: 1 }, { id: 'kmh', label: 'km/h', f: 0.277778 },
      { id: 'mph', label: 'mph', f: 0.44704 }, { id: 'fts', label: 'ft/s', f: 0.3048 },
      { id: 'knot', label: 'knot', f: 0.514444 },
    ],
  },
  time: {
    name: { es: 'Tiempo', en: 'Time' },
    units: [
      { id: 'ms', label: 'ms', f: 0.001 }, { id: 's', label: 's', f: 1 },
      { id: 'min', label: 'min', f: 60 }, { id: 'h', label: 'h', f: 3600 },
      { id: 'd', label: 'día', f: 86400 }, { id: 'wk', label: 'semana', f: 604800 },
    ],
  },
  data: {
    name: { es: 'Datos', en: 'Data' },
    units: [
      { id: 'B', label: 'B', f: 1 }, { id: 'KB', label: 'KB', f: 1024 },
      { id: 'MB', label: 'MB', f: 1048576 }, { id: 'GB', label: 'GB', f: 1073741824 },
      { id: 'TB', label: 'TB', f: 1099511627776 },
    ],
  },
};

const tempToC = (v, u) => (u === 'C' ? v : u === 'F' ? (v - 32) * 5 / 9 : v - 273.15);
const tempFromC = (c, u) => (u === 'C' ? c : u === 'F' ? c * 9 / 5 + 32 : c + 273.15);

function convert(value, catKey, from, to) {
  const cat = CONV[catKey];
  if (!cat || !isFinite(value)) return NaN;
  if (cat.special === 'temp') return tempFromC(tempToC(value, from), to);
  const fu = cat.units.find((u) => u.id === from);
  const tu = cat.units.find((u) => u.id === to);
  if (!fu || !tu) return NaN;
  return (value * fu.f) / tu.f;
}

// Compact, locale-free number formatter that avoids float noise and keeps long
// or tiny results readable (scientific only at the extremes).
function fmtNum(n) {
  if (n === '' || n == null) return '';
  if (!isFinite(n)) return 'Error';
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1e15 || abs < 1e-9) return n.toExponential(6);
  const r = Number(n.toPrecision(12));
  return String(r);
}

// Money formatter ($1,234.56). Used by the Commission and Currency tabs.
function money(n) {
  if (!isFinite(n)) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const OPS = { '+': (a, b) => a + b, '-': (a, b) => a - b, '×': (a, b) => a * b, '÷': (a, b) => (b === 0 ? NaN : a / b) };

const HISTORY_KEY = 'dkt_calc_history_v1';
const FX_KEY = 'dkt_calc_fx_v1';
const FX_URL = 'https://open.er-api.com/v6/latest/USD';

export function CalculatorWidget({ lang = 'en', onClose, userPrefs = {}, onPrefsChange }) {
  const es = lang === 'es';
  const [tab, setTab] = useState('calc');

  // Default position: under the header, toward the right. Draggable from there.
  const [pos, setPos] = useState(() => ({
    x: Math.max(12, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 360),
    y: 70,
  }));
  const drag = useRef(null);
  const onHeaderDown = (e) => {
    if (e.target.closest('[data-no-drag]')) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.preventDefault();
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const w = window.innerWidth, h = window.innerHeight;
      setPos({
        x: Math.min(Math.max(0, e.clientX - drag.current.dx), w - 80),
        y: Math.min(Math.max(0, e.clientY - drag.current.dy), h - 40),
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  // ── History (persisted in uiPrefs → server, follows the user across devices;
  // was localStorage-only). Falls back to [] when prefs aren't wired. ──────────
  const history = Array.isArray(userPrefs?.calcHistory) ? userPrefs.calcHistory : [];
  const idSeed = useRef(1);
  const pushHistory = (kind, text, value) => {
    if (!onPrefsChange) return;
    onPrefsChange((p) => {
      const prev = Array.isArray(p?.calcHistory) ? p.calcHistory : [];
      const next = [{ id: `h${Date.now()}_${idSeed.current++}`, kind, text, value: String(value) }, ...prev].slice(0, 40);
      return { ...p, calcHistory: next };
    });
  };
  const clearHistory = () => { if (onPrefsChange) onPrefsChange((p) => ({ ...p, calcHistory: [] })); };

  // ── Calculator state (classic accumulator model) ─────────────────────────
  const [display, setDisplay] = useState('0');
  const [acc, setAcc] = useState(null);
  const [op, setOp] = useState(null);
  const [overwrite, setOverwrite] = useState(true);

  const inputDigit = (d) => {
    setOverwrite(false);
    setDisplay((p) => {
      if (overwrite || p === 'Error') return d;
      if (p === '0') return d;
      if (p.replace(/[^0-9]/g, '').length >= 15) return p;
      return p + d;
    });
  };
  const inputDecimal = () => {
    setDisplay((p) => {
      if (overwrite || p === 'Error') { setOverwrite(false); return '0.'; }
      return p.includes('.') ? p : p + '.';
    });
    setOverwrite(false);
  };
  const clearAll = () => { setDisplay('0'); setAcc(null); setOp(null); setOverwrite(true); };
  const backspace = () => {
    if (overwrite) return;
    setDisplay((p) => {
      if (p === 'Error') return '0';
      const n = p.length > 1 ? p.slice(0, -1) : '0';
      return n === '' || n === '-' ? '0' : n;
    });
  };
  const toggleSign = () => setDisplay((p) => (p === '0' || p === 'Error' ? p : p.startsWith('-') ? p.slice(1) : '-' + p));
  const percent = () => { setDisplay((p) => fmtNum(parseFloat(p) / 100)); setOverwrite(true); };
  const chooseOp = (nextOp) => {
    const cur = parseFloat(display);
    if (acc === null) {
      setAcc(cur);
    } else if (!overwrite) {
      const r = OPS[op](acc, cur);
      setAcc(r);
      setDisplay(fmtNum(r));
    }
    setOp(nextOp);
    setOverwrite(true);
  };
  const equals = () => {
    if (op === null || acc === null) return;
    const cur = parseFloat(display);
    const r = OPS[op](acc, cur);
    setDisplay(fmtNum(r));
    pushHistory('calc', `${fmtNum(acc)} ${op} ${fmtNum(cur)} =`, fmtNum(r));
    setAcc(null); setOp(null); setOverwrite(true);
  };

  // Keyboard support — SCOPED to the panel (onKeyDown below), so typing in CRM
  // forms is never hijacked. The panel autofocuses when the Calculator tab opens.
  const calcRef = useRef(null);
  useEffect(() => { if (tab === 'calc' && calcRef.current) calcRef.current.focus(); }, [tab]);
  const onKey = (e) => {
    const k = e.key;
    if (k >= '0' && k <= '9') { inputDigit(k); e.preventDefault(); }
    else if (k === '.') { inputDecimal(); e.preventDefault(); }
    else if (k === '+') { chooseOp('+'); e.preventDefault(); }
    else if (k === '-') { chooseOp('-'); e.preventDefault(); }
    else if (k === '*') { chooseOp('×'); e.preventDefault(); }
    else if (k === '/') { chooseOp('÷'); e.preventDefault(); }
    else if (k === 'Enter' || k === '=') { equals(); e.preventDefault(); }
    else if (k === 'Backspace') { backspace(); e.preventDefault(); }
    else if (k === '%') { percent(); e.preventDefault(); }
    else if (k === 'Escape') { onClose && onClose(); }
    else if (k === 'c' || k === 'C') { clearAll(); }
  };
  // Send a value from another tab / history into the calculator.
  const useValue = (v) => { setDisplay(String(v)); setAcc(null); setOp(null); setOverwrite(true); setTab('calc'); };

  // ── Unit converter state ─────────────────────────────────────────────────
  const [cat, setCat] = useState('length');
  const [from, setFrom] = useState('m');
  const [to, setTo] = useState('ft');
  const [val, setVal] = useState('1');
  const onCat = (key) => {
    setCat(key);
    const u = CONV[key].units;
    setFrom(u[0].id);
    setTo(u[1] ? u[1].id : u[0].id);
  };
  const swap = () => { setFrom(to); setTo(from); };
  const result = useMemo(() => {
    const v = parseFloat(val);
    if (val === '' || isNaN(v)) return '';
    return fmtNum(convert(v, cat, from, to));
  }, [val, cat, from, to]);

  // ── Commission state ─────────────────────────────────────────────────────
  const [comAmt, setComAmt] = useState('');
  const [comPct, setComPct] = useState('');
  const [comFreq, setComFreq] = useState('monthly'); // monthly | annual
  const com = useMemo(() => {
    const amt = parseFloat(comAmt); const pct = parseFloat(comPct);
    if (isNaN(amt) || isNaN(pct)) return null;
    const periodCommission = amt * pct / 100;
    const annualPremium = comFreq === 'monthly' ? amt * 12 : amt;
    const annualCommission = annualPremium * pct / 100;
    return { periodCommission, annualPremium, annualCommission };
  }, [comAmt, comPct, comFreq]);

  // ── Currency state ───────────────────────────────────────────────────────
  // FX rates cache now lives in uiPrefs (server) instead of localStorage, so the
  // last-known rates follow the user across devices and nothing sits locally.
  const fx = (userPrefs && userPrefs.calcFx) || null;
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(false);
  const [cFrom, setCFrom] = useState('USD');
  const [cTo, setCTo] = useState('MXN');
  const [cAmt, setCAmt] = useState('1');
  const loadFx = async () => {
    setFxLoading(true); setFxError(false);
    try {
      const r = await fetch(FX_URL);
      const j = await r.json();
      if (j && j.result === 'success' && j.rates) {
        const data = { rates: j.rates, date: j.time_last_update_utc || '' };
        if (onPrefsChange) onPrefsChange((p) => ({ ...p, calcFx: data }));
      } else { throw new Error('bad payload'); }
    } catch { setFxError(true); }
    finally { setFxLoading(false); }
  };
  // Fetch when the Currency tab opens if we have nothing cached.
  useEffect(() => { if (tab === 'currency' && !fx && !fxLoading) loadFx(); /* eslint-disable-next-line */ }, [tab]);
  const fxCurrencies = useMemo(() => (fx && fx.rates ? Object.keys(fx.rates).sort() : ['USD', 'MXN', 'EUR']), [fx]);
  const fxResult = useMemo(() => {
    if (!fx || !fx.rates) return null;
    const a = parseFloat(cAmt);
    if (isNaN(a)) return null;
    const rf = fx.rates[cFrom], rt = fx.rates[cTo];
    if (!rf || !rt) return null;
    return a * (rt / rf);
  }, [fx, cAmt, cFrom, cTo]);

  // ── Styles ───────────────────────────────────────────────────────────────
  const btn = (bg, color) => ({
    border: '1px solid var(--c-border,#1a3550)', background: bg, color,
    borderRadius: 10, padding: '.6rem 0', fontSize: '1.05rem', fontWeight: 700,
    cursor: 'pointer', fontFamily: 'var(--font-mono,monospace)', userSelect: 'none', transition: 'filter .1s',
    // Center every key's content. Text keys center by default, but the ⌫
    // (Delete icon) sat off to the side/baseline without flex — it looked
    // broken (Lissette 2026-06-22 "ese botón centralo o arreglalo").
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });
  const numBg = 'var(--c-surface2,#112538)';
  const opBg = 'rgba(0,212,255,.14)';
  const Key = ({ label, onClick, kind = 'num' }) => (
    <button data-no-drag onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      style={btn(kind === 'op' ? opBg : kind === 'eq' ? 'linear-gradient(135deg,#0099cc,#0055aa)' : kind === 'fn' ? 'var(--c-surface,#0d1c2e)' : numBg,
        kind === 'op' ? 'var(--c-cyan,#00d4ff)' : kind === 'eq' ? '#fff' : kind === 'fn' ? 'var(--c-text2,#7aa3c0)' : 'var(--c-white,#eef6ff)')}
      onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.15)')}
      onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}>{label}</button>
  );
  const sel = {
    width: '100%', padding: '.5rem .6rem', borderRadius: 8, cursor: 'pointer',
    background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
    color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', fontFamily: 'inherit',
  };
  const inp = { ...sel, textAlign: 'right', fontFamily: 'var(--font-mono,monospace)', cursor: 'text' };
  const lbl = { fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.05em' };
  const resultBox = (color) => ({ ...sel, textAlign: 'right', fontFamily: 'var(--font-mono,monospace)', color, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' });
  const saveBtn = (onClick) => (
    <button onClick={onClick} title={es ? 'Guardar en historial' : 'Save to history'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(0,255,136,.1)', border: '1px solid rgba(0,255,136,.3)', color: 'var(--c-green,#00ff88)', borderRadius: 8, padding: '.35rem .7rem', cursor: 'pointer', fontSize: '.74rem', fontWeight: 700 }}>
      <Save size={13} /> {es ? 'Guardar' : 'Save'}
    </button>
  );

  const TABS = [
    ['calc', es ? 'Calc.' : 'Calc'],
    ['units', es ? 'Unidades' : 'Units'],
    ['commission', es ? 'Comisión' : 'Commission'],
    ['currency', es ? 'Moneda' : 'Currency'],
    ['history', es ? 'Historial' : 'History'],
  ];

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, width: 340, zIndex: 1000,
      background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border2,#1f3f60)',
      borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,.55)', overflow: 'hidden', fontFamily: 'system-ui,sans-serif',
    }}>
      {/* Drag handle / header */}
      <div onMouseDown={onHeaderDown} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '.6rem .8rem',
        background: 'linear-gradient(135deg,rgba(0,212,255,.12),rgba(0,0,0,.18))',
        borderBottom: '1px solid var(--c-border,#1a3550)', cursor: 'move',
      }}>
        <Calculator size={15} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <span style={{ fontSize: '.82rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', flex: 1 }}>
          {es ? 'Calculadora' : 'Calculator'}
        </span>
        <button data-no-drag onClick={onClose} title={es ? 'Cerrar' : 'Close'}
          style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', display: 'flex', padding: 2 }}><X size={16} /></button>
      </div>

      {/* Tabs (wrap so 5 fit) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '.5rem .7rem 0' }}>
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            flex: '1 0 auto', padding: '.35rem .5rem', borderRadius: 8, cursor: 'pointer', fontSize: '.72rem', fontWeight: 700,
            background: tab === id ? 'rgba(0,212,255,.12)' : 'transparent',
            border: tab === id ? '1px solid rgba(0,212,255,.4)' : '1px solid transparent',
            color: tab === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
          }}>{label}</button>
        ))}
      </div>

      {/* ── CALCULATOR ── */}
      {tab === 'calc' && (
        <div ref={calcRef} tabIndex={0} onKeyDown={onKey} style={{ padding: '.7rem', outline: 'none' }}>
          <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.6rem .8rem', marginBottom: '.6rem', textAlign: 'right' }}>
            <div style={{ minHeight: 14, fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono,monospace)' }}>
              {acc !== null ? `${fmtNum(acc)} ${op || ''}` : ' '}
            </div>
            <div style={{ fontSize: '1.7rem', fontWeight: 800, color: display === 'Error' ? 'var(--c-red,#ff3d6b)' : 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-mono,monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
            <Key label="C" kind="fn" onClick={clearAll} />
            <Key label={<Delete size={16} />} kind="fn" onClick={backspace} />
            <Key label="%" kind="fn" onClick={percent} />
            <Key label="÷" kind="op" onClick={() => chooseOp('÷')} />
            <Key label="7" onClick={() => inputDigit('7')} />
            <Key label="8" onClick={() => inputDigit('8')} />
            <Key label="9" onClick={() => inputDigit('9')} />
            <Key label="×" kind="op" onClick={() => chooseOp('×')} />
            <Key label="4" onClick={() => inputDigit('4')} />
            <Key label="5" onClick={() => inputDigit('5')} />
            <Key label="6" onClick={() => inputDigit('6')} />
            <Key label="−" kind="op" onClick={() => chooseOp('-')} />
            <Key label="1" onClick={() => inputDigit('1')} />
            <Key label="2" onClick={() => inputDigit('2')} />
            <Key label="3" onClick={() => inputDigit('3')} />
            <Key label="+" kind="op" onClick={() => chooseOp('+')} />
            <Key label="±" kind="fn" onClick={toggleSign} />
            <Key label="0" onClick={() => inputDigit('0')} />
            <Key label="." onClick={inputDecimal} />
            <Key label="=" kind="eq" onClick={equals} />
          </div>
          <div style={{ marginTop: 8, fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textAlign: 'center' }}>
            {es ? 'Tip: podés escribir con el teclado' : 'Tip: you can type with your keyboard'}
          </div>
        </div>
      )}

      {/* ── UNIT CONVERTER ── */}
      {tab === 'units' && (
        <div style={{ padding: '.7rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          <div>
            <label style={lbl}>{es ? 'Categoría' : 'Category'}</label>
            <select value={cat} onChange={(e) => onCat(e.target.value)} style={sel}>
              {Object.entries(CONV).map(([k, c]) => <option key={k} value={k}>{es ? c.name.es : c.name.en}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>{es ? 'De' : 'From'}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <input value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9.\-]/g, ''))} inputMode="decimal" style={inp} />
              <select value={from} onChange={(e) => setFrom(e.target.value)} style={sel}>
                {CONV[cat].units.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button onClick={swap} title={es ? 'Intercambiar' : 'Swap'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '.3rem .7rem', cursor: 'pointer', fontSize: '.74rem', fontWeight: 700 }}>
              <ArrowLeftRight size={13} /> {es ? 'Intercambiar' : 'Swap'}
            </button>
          </div>
          <div>
            <label style={lbl}>{es ? 'A' : 'To'}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div style={resultBox('var(--c-green,#00ff88)')}>{result || '—'}</div>
              <select value={to} onChange={(e) => setTo(e.target.value)} style={sel}>
                {CONV[cat].units.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </div>
          </div>
          {result !== '' && result !== 'Error' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono,monospace)' }}>
                {val} {CONV[cat].units.find((u) => u.id === from)?.label} = {result} {CONV[cat].units.find((u) => u.id === to)?.label}
              </div>
              {saveBtn(() => pushHistory('units', `${val} ${CONV[cat].units.find((u) => u.id === from)?.label} → ${CONV[cat].units.find((u) => u.id === to)?.label}`, `${result} ${CONV[cat].units.find((u) => u.id === to)?.label}`))}
            </div>
          )}
        </div>
      )}

      {/* ── COMMISSION ── */}
      {tab === 'commission' && (
        <div style={{ padding: '.7rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          <div>
            <label style={lbl}>{es ? 'Monto de la prima' : 'Premium amount'}</label>
            <input value={comAmt} onChange={(e) => setComAmt(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="$" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[['monthly', es ? 'Mensual' : 'Monthly'], ['annual', es ? 'Anual' : 'Annual']].map(([id, label]) => (
              <button key={id} onClick={() => setComFreq(id)} style={{
                padding: '.45rem 0', borderRadius: 8, cursor: 'pointer', fontSize: '.78rem', fontWeight: 700,
                background: comFreq === id ? 'rgba(0,212,255,.14)' : 'var(--c-surface2,#112538)',
                border: comFreq === id ? '1px solid rgba(0,212,255,.4)' : '1px solid var(--c-border,#1a3550)',
                color: comFreq === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
              }}>{label}</button>
            ))}
          </div>
          <div>
            <label style={lbl}>{es ? '% Comisión' : 'Commission %'}</label>
            <input value={comPct} onChange={(e) => setComPct(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder={es ? 'Ej: 12.5' : 'e.g. 12.5'} style={inp} />
          </div>
          <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.7rem .85rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              [comFreq === 'monthly' ? (es ? 'Comisión mensual' : 'Monthly commission') : (es ? 'Comisión' : 'Commission'), com ? money(com.periodCommission) : '—', 'var(--c-green,#00ff88)'],
              [es ? 'Prima anual' : 'Annual premium', com ? money(com.annualPremium) : '—', 'var(--c-text,#c8dff0)'],
              [es ? 'Comisión anual' : 'Annual commission', com ? money(com.annualCommission) : '—', 'var(--c-green,#00ff88)'],
            ].map(([k, v, c], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>{k}</span>
                <span style={{ fontSize: i === 0 ? '1.15rem' : '.9rem', fontWeight: 800, color: c, fontFamily: 'var(--font-mono,monospace)' }}>{v}</span>
              </div>
            ))}
          </div>
          {com && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {saveBtn(() => pushHistory('commission', `${es ? 'Comisión' : 'Commission'} ${comPct}% ${es ? 'de' : 'of'} ${money(parseFloat(comAmt))}`, money(com.periodCommission)))}
            </div>
          )}
        </div>
      )}

      {/* ── CURRENCY ── */}
      {tab === 'currency' && (
        <div style={{ padding: '.7rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          <div>
            <label style={lbl}>{es ? 'De' : 'From'}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <input value={cAmt} onChange={(e) => setCAmt(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" style={inp} />
              <select value={cFrom} onChange={(e) => setCFrom(e.target.value)} style={sel}>
                {fxCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button onClick={() => { setCFrom(cTo); setCTo(cFrom); }} title={es ? 'Intercambiar' : 'Swap'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '.3rem .7rem', cursor: 'pointer', fontSize: '.74rem', fontWeight: 700 }}>
              <ArrowLeftRight size={13} /> {es ? 'Intercambiar' : 'Swap'}
            </button>
          </div>
          <div>
            <label style={lbl}>{es ? 'A' : 'To'}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div style={resultBox('var(--c-green,#00ff88)')}>{fxResult != null ? fmtNum(Number(fxResult.toFixed(4))) : '—'}</div>
              <select value={cTo} onChange={(e) => setCTo(e.target.value)} style={sel}>
                {fxCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
              {fxLoading ? (es ? 'Cargando tasas…' : 'Loading rates…')
                : fxError && !fx ? (es ? 'Sin conexión a las tasas' : 'Could not load rates')
                : fx ? `${es ? 'Tasas' : 'Rates'}: ${String(fx.date || '').replace(/\s\d{2}:\d{2}.*$/, '')}${fxError ? (es ? ' (sin conexión)' : ' (offline)') : ''}`
                : '—'}
            </div>
            <button onClick={loadFx} disabled={fxLoading} title={es ? 'Actualizar' : 'Refresh'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 7, padding: '.25rem .55rem', cursor: fxLoading ? 'wait' : 'pointer', fontSize: '.7rem', fontWeight: 600 }}>
              <RefreshCw size={11} /> {es ? 'Actualizar' : 'Refresh'}
            </button>
          </div>
          {fxResult != null && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono,monospace)' }}>
                {cAmt} {cFrom} = {fmtNum(Number(fxResult.toFixed(4)))} {cTo}
              </div>
              {saveBtn(() => pushHistory('currency', `${cAmt} ${cFrom} → ${cTo}`, `${fmtNum(Number(fxResult.toFixed(2)))} ${cTo}`))}
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY ── */}
      {tab === 'history' && (
        <div style={{ padding: '.7rem', display: 'flex', flexDirection: 'column', gap: '.5rem', maxHeight: 360, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.75rem', fontWeight: 700, color: 'var(--c-text2,#7aa3c0)' }}>
              <Clock size={13} /> {history.length} {es ? 'cálculos' : 'entries'}
            </span>
            {history.length > 0 && (
              <button onClick={clearHistory} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text3,#45688a)', borderRadius: 7, padding: '.25rem .55rem', cursor: 'pointer', fontSize: '.7rem', fontWeight: 600 }}>
                <Trash2 size={11} /> {es ? 'Limpiar' : 'Clear'}
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem .5rem', color: 'var(--c-text3,#45688a)', fontSize: '.78rem' }}>
              {es ? 'Aún no hay cálculos. Tus resultados aparecerán aquí.' : 'No entries yet. Your results will show up here.'}
            </div>
          ) : history.map((h) => (
            <button key={h.id} onClick={() => useValue(String(h.value).replace(/[^0-9.\-]/g, '') || h.value)} title={es ? 'Usar este valor' : 'Use this value'}
              style={{ textAlign: 'left', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.5rem .65rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2 }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(0,212,255,.4)')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)')}>
              <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono,monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.text}</span>
              <span style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-mono,monospace)' }}>{h.value}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
